import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Users, Heart, Leaf, Globe, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ImpactPage = () => {
  const [stats, setStats] = useState(null);
  const [stories, setStories] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, storiesRes] = await Promise.all([
        axios.get(`${API}/impact/stats`),
        axios.get(`${API}/impact/stories`)
      ]);
      setStats(statsRes.data);
      setStories(storiesRes.data || []);
    } catch (error) {
      console.error('Error fetching impact data:', error);
    }
  };

  return (
    <div className="pt-24 pb-24" data-testid="impact-page">
      {/* Hero Banner */}
      <section className="relative h-[50vh] min-h-[400px] mb-24">
        <img 
          src="/images/banners/impact-banner.png" 
          alt="Artisan craftsmanship" 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-transparent" />
        <div className="absolute inset-0 flex items-center">
          <div className="chaleur-container">
            <div className="max-w-2xl text-white">
              <p className="caption text-white/80 mb-4">Our Impact</p>
              <h1 className="text-4xl md:text-5xl font-light mb-6">
                The Ripple Effect of <span className="editorial-italic">Every</span> Purchase
              </h1>
              <p className="text-white/80 leading-relaxed">
                At Chaleur, we believe commerce can be a force for good. Every scarf purchased, 
                every gift given, creates ripples of positive change that extend far beyond the 
                product itself—empowering artisans, preserving traditions, and nurturing communities.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-spacing bg-secondary/30">
        <div className="chaleur-container">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 flex items-center justify-center">
                <Users className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {stats?.artisans_supported || 200}+
              </div>
              <p className="caption text-muted-foreground">Artisans Supported</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 flex items-center justify-center">
                <Heart className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {stats?.families_impacted || 500}+
              </div>
              <p className="caption text-muted-foreground">Families Impacted</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 flex items-center justify-center">
                <Leaf className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {stats?.sustainable_materials_kg || 5000}kg
              </div>
              <p className="caption text-muted-foreground">Sustainable Materials</p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-primary/10 flex items-center justify-center">
                <Globe className="w-8 h-8 text-primary" />
              </div>
              <div className="text-4xl md:text-5xl font-light text-primary mb-2">
                {stats?.carbon_offset_tons || 50}t
              </div>
              <p className="caption text-muted-foreground">Carbon Offset</p>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Areas */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-primary mb-4">How We Give Back</p>
            <h2 className="text-3xl md:text-4xl font-light">Our Impact Areas</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="p-8 border border-border hover:border-primary/30 transition-colors">
              <h3 className="text-xl mb-4">Artisan Empowerment</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                We provide fair wages, skill development, and sustainable employment 
                to artisan communities across India, ensuring traditional crafts thrive 
                for generations to come.
              </p>
              <Link to="/artisans" className="text-primary text-sm hover:underline inline-flex items-center">
                Meet Our Artisans <ChevronRight className="w-4 h-4 ml-1" />
              </Link>
            </div>
            <div className="p-8 border border-border hover:border-primary/30 transition-colors">
              <h3 className="text-xl mb-4">Community Development</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                A portion of every sale goes toward education, healthcare, and 
                infrastructure projects in the communities where our artisans live 
                and work.
              </p>
              <span className="text-primary text-sm">₹{(stats?.donations_made || 1500000).toLocaleString()} contributed</span>
            </div>
            <div className="p-8 border border-border hover:border-primary/30 transition-colors">
              <h3 className="text-xl mb-4">Sustainable Practices</h3>
              <p className="text-muted-foreground text-sm leading-relaxed mb-6">
                We use eco-friendly materials, natural dyes, and sustainable packaging 
                to minimize our environmental footprint while maintaining the highest 
                quality standards.
              </p>
              <span className="text-primary text-sm">100% plastic-free packaging</span>
            </div>
          </div>
        </div>
      </section>

      {/* Community Stories */}
      <section className="section-spacing bg-foreground text-primary-foreground">
        <div className="chaleur-container">
          <div className="text-center mb-16">
            <p className="caption text-white/60 mb-4">Faces of Warmth</p>
            <h2 className="text-3xl md:text-4xl font-light">Stories from Our Community</h2>
          </div>

          <div className="grid md:grid-cols-2 gap-12">
            <div className="flex gap-6">
              <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1607746882042-944635dfe10e?w=200&q=80"
                  alt="Artisan"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white/80 italic mb-4">
                  "Working with Chaleur has transformed my family's life. My children 
                  can now go to school, and I can practice the craft my grandmother 
                  taught me."
                </p>
                <p className="text-white font-medium">Lakshmi Devi</p>
                <p className="text-white/60 text-sm">Master Weaver, Gujarat</p>
              </div>
            </div>
            <div className="flex gap-6">
              <div className="w-24 h-24 rounded-full overflow-hidden flex-shrink-0">
                <img 
                  src="https://images.unsplash.com/photo-1581803118522-7b72a50f7e9f?w=200&q=80"
                  alt="Artisan"
                  className="w-full h-full object-cover"
                />
              </div>
              <div>
                <p className="text-white/80 italic mb-4">
                  "For 40 years I've been weaving, but only now do I feel my work 
                  is truly valued. Chaleur understands the art behind each thread."
                </p>
                <p className="text-white font-medium">Ramesh Kumar</p>
                <p className="text-white/60 text-sm">Traditional Block Printer, Rajasthan</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-spacing">
        <div className="chaleur-container">
          <div className="max-w-3xl mx-auto text-center">
            <p className="caption text-primary mb-4">Join the Movement</p>
            <h2 className="text-3xl md:text-4xl font-light mb-6">
              Be Part of Something Meaningful
            </h2>
            <p className="text-muted-foreground mb-8">
              Every Chaleur purchase is a vote for a better world—one where craftsmanship 
              is valued, communities thrive, and giving is celebrated.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild className="btn-chaleur-primary">
                <Link to="/shop">Shop with Purpose</Link>
              </Button>
              <Button asChild variant="outline" className="btn-chaleur-secondary">
                <Link to="/artisans">Meet the Artisans</Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ImpactPage;
